import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Paper,
  Tabs,
  Tab
} from '@mui/material'
import {
  Search as SearchIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material'
import SearchComponent from '../../components/SearchComponent'
import { EnhancedSearchResult } from '../../services/searchService'
import { searchService } from '../../services/searchService'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

export default function SearchPage() {
  const [tabValue, setTabValue] = useState(0)
  const [selectedProduct, setSelectedProduct] = useState<EnhancedSearchResult | null>(null)
  const [dataLoaded, setDataLoaded] = useState(false)

  useEffect(() => {
    // Component mounted
    setDataLoaded(true)
  }, [])
  const [productDialogOpen, setProductDialogOpen] = useState(false)
  const [searchStats, setSearchStats] = useState<any>(null)
  const [categoryStats, setCategoryStats] = useState<any>(null)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleProductSelect = (product: EnhancedSearchResult) => {
    setSelectedProduct(product)
    setProductDialogOpen(true)
  }

  const handleRequestPrice = (product: EnhancedSearchResult) => {
    // Handle price request
    console.log('Request price for:', product)
    alert(`تم طلب عرض سعر للمنتج: ${product.title}`)
  }

  const handleSearchStats = (stats: any) => {
    setSearchStats(stats)
  }

  const handleCategoryStats = (stats: any) => {
    setCategoryStats(stats)
  }

  const handleCloseProductDialog = () => {
    setProductDialogOpen(false)
    setSelectedProduct(null)
  }

  const handleApproveCategory = (product: EnhancedSearchResult) => {
    // Handle category approval
    console.log('Approve category for:', product)
  }

  const handleRejectCategory = (product: EnhancedSearchResult) => {
    // Handle category rejection
    console.log('Reject category for:', product)
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          بحث المنتجات
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<AnalyticsIcon />}
            onClick={() => setTabValue(2)}
          >
            الإحصائيات
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setTabValue(3)}
          >
            الإعدادات
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="search tabs">
          <Tab icon={<SearchIcon />} label="البحث" />
          <Tab icon={<CategoryIcon />} label="إدارة الفئات" />
          <Tab icon={<AnalyticsIcon />} label="الإحصائيات" />
          <Tab icon={<SettingsIcon />} label="الإعدادات" />
        </Tabs>
      </Paper>

      {/* Search Tab */}
      <TabPanel value={tabValue} index={0}>
        <SearchComponent
          onProductSelect={handleProductSelect}
          onRequestPrice={handleRequestPrice}
          showStats={true}
          maxResults={20}
        />
      </TabPanel>

      {/* Category Management Tab */}
      <TabPanel value={tabValue} index={1}>
        <CategoryManagementTab />
      </TabPanel>

      {/* Analytics Tab */}
      <TabPanel value={tabValue} index={2}>
        <AnalyticsTab 
          searchStats={searchStats}
          categoryStats={categoryStats}
        />
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel value={tabValue} index={3}>
        <SettingsTab />
      </TabPanel>

      {/* Product Details Dialog */}
      <Dialog 
        open={productDialogOpen} 
        onClose={handleCloseProductDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          تفاصيل المنتج
        </DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedProduct.title}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" paragraph>
                {selectedProduct.snippet}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    الموقع:
                  </Typography>
                  <Typography variant="body2">
                    {selectedProduct.displayLink}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    الرابط:
                  </Typography>
                  <Typography variant="body2">
                    <a href={selectedProduct.link} target="_blank" rel="noopener noreferrer">
                      {selectedProduct.formattedUrl}
                    </a>
                  </Typography>
                </Grid>

                {selectedProduct.brand && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      العلامة التجارية:
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.brand}
                    </Typography>
                  </Grid>
                )}

                {selectedProduct.price && selectedProduct.price > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      السعر:
                    </Typography>
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                      {selectedProduct.price.toLocaleString()} ج.م
                    </Typography>
                  </Grid>
                )}

                {selectedProduct.category && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      الفئة المقترحة:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip 
                        label={selectedProduct.category} 
                        color="primary"
                        size="small"
                      />
                      {selectedProduct.categoryConfidence && (
                        <Typography variant="caption" color="textSecondary">
                          (ثقة: {Math.round(selectedProduct.categoryConfidence * 100)}%)
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {selectedProduct.categoryReasoning && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      سبب التصنيف:
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.categoryReasoning}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {selectedProduct.imageUrl && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <img 
                    src={selectedProduct.imageUrl} 
                    alt={selectedProduct.title}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 300, 
                      objectFit: 'contain',
                      borderRadius: 8
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductDialog}>
            إغلاق
          </Button>
          <Button 
            variant="contained" 
            onClick={() => selectedProduct && handleRequestPrice(selectedProduct)}
          >
            طلب عرض سعر
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Category Management Component
function CategoryManagementTab() {
  const [categories, setCategories] = useState<string[]>([])
  const [newCategory, setNewCategory] = useState('')
  const [editingCategory, setEditingCategory] = useState<string | null>(null)
  const [editingCategoryValue, setEditingCategoryValue] = useState('')

  React.useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      const cats = await searchService.getAvailableCategories()
      setCategories(cats)
      console.log('📋 Categories loaded:', cats)
    } catch (error) {
      console.error('❌ Error loading categories:', error)
      // Fallback to default categories
      setCategories([
        'موبايل وتابلت', 'تلفزيونات', 'أجهزة كبيرة', 'أجهزة صغيرة',
        'أجهزة المطبخ', 'كمبيوتر', 'الصحة والجمال', 'إلكترونيات',
        'أدوات المنزل', 'ملابس', 'أحذية', 'حقائب', 'ساعات', 'مجوهرات'
      ])
    }
  }

  const handleAddCategory = async () => {
    const trimmedCategory = newCategory.trim()
    if (trimmedCategory && !categories.includes(trimmedCategory)) {
      try {
        // Save to Firestore
        const success = await searchService.addCategory(trimmedCategory)
        if (success) {
          // Reload categories from Firestore to get updated list
          await loadCategories()
          setNewCategory('')
          console.log('✅ Category added successfully')
        } else {
          alert('فشل في إضافة الفئة. قد تكون الفئة موجودة بالفعل.')
        }
      } catch (error) {
        console.error('❌ Error adding category:', error)
        alert('حدث خطأ أثناء إضافة الفئة. يرجى المحاولة مرة أخرى.')
      }
    }
  }

  const handleEditCategory = async (oldCategory: string, newCategory: string) => {
    if (newCategory.trim() && newCategory.trim() !== oldCategory) {
      try {
        // Update in Firestore
        const success = await searchService.updateCategory(oldCategory, newCategory.trim())
        if (success) {
          // Reload categories from Firestore to get updated list
          await loadCategories()
          setEditingCategory(null)
          console.log('✅ Category updated successfully')
        } else {
          alert('فشل في تحديث الفئة. قد تكون الفئة الجديدة موجودة بالفعل.')
        }
      } catch (error) {
        console.error('❌ Error updating category:', error)
        alert('حدث خطأ أثناء تحديث الفئة. يرجى المحاولة مرة أخرى.')
      }
    } else {
      setEditingCategory(null)
    }
  }

  const handleDeleteCategory = async (category: string) => {
    if (window.confirm(`هل أنت متأكد من حذف الفئة "${category}"؟`)) {
      try {
        // Delete from Firestore
        const success = await searchService.deleteCategory(category)
        if (success) {
          // Reload categories from Firestore to get updated list
          await loadCategories()
          console.log('✅ Category deleted successfully')
        } else {
          alert('فشل في حذف الفئة. قد تكون الفئة غير موجودة.')
        }
      } catch (error) {
        console.error('❌ Error deleting category:', error)
        alert('حدث خطأ أثناء حذف الفئة. يرجى المحاولة مرة أخرى.')
      }
    }
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        إدارة الفئات
      </Typography>

      {/* Add New Category */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            إضافة فئة جديدة
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              label="اسم الفئة"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddCategory()
                }
              }}
            />
            <Button 
              variant="contained" 
              onClick={handleAddCategory}
              disabled={!newCategory.trim()}
            >
              إضافة
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Categories List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            الفئات المتاحة ({categories.length})
          </Typography>
          
          <Grid container spacing={2}>
            {categories.map((category, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {editingCategory === category ? (
                    <TextField
                      size="small"
                      value={editingCategoryValue}
                      onChange={(e) => setEditingCategoryValue(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleEditCategory(category, editingCategoryValue)
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <Typography variant="body1">
                      {category}
                    </Typography>
                  )}
                  
                  <Box>
                    {editingCategory === category ? (
                      <>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditCategory(category, editingCategoryValue)}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditingCategory(null)
                            setEditingCategoryValue('')
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditingCategory(category)
                            setEditingCategoryValue(category)
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteCategory(category)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

// Analytics Component
function AnalyticsTab({ searchStats, categoryStats }: { searchStats: any, categoryStats: any }) {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        إحصائيات البحث
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                إحصائيات البحث
              </Typography>
              {searchStats ? (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    إجمالي النتائج: {searchStats.totalResults?.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    النتائج المعالجة: {searchStats.processedResults}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    تصنيف بالذكاء الاصطناعي: {searchStats.aiCategorizations}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    وقت البحث: {searchStats.searchTime}ms
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  لا توجد إحصائيات متاحة
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                معلومات الكاش
              </Typography>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Google Search: {searchService.getCacheInfo().googleSearch} عنصر
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  AI Category: {searchService.getCacheInfo().aiCategory} عنصر
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

// Settings Component
function SettingsTab() {
  const [settings, setSettings] = useState({
    enableAICategorization: true,
    maxResults: 20,
    cacheDuration: 5,
    autoRefresh: false
  })

  const handleClearCache = () => {
    searchService.clearCaches()
    alert('تم مسح الكاش بنجاح')
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        إعدادات محرك البحث
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            الإعدادات العامة
          </Typography>
          
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="الحد الأقصى للنتائج"
              type="number"
              value={settings.maxResults}
              onChange={(e) => setSettings({...settings, maxResults: parseInt(e.target.value)})}
              sx={{ maxWidth: 200 }}
            />
            
            <TextField
              label="مدة الكاش (دقائق)"
              type="number"
              value={settings.cacheDuration}
              onChange={(e) => setSettings({...settings, cacheDuration: parseInt(e.target.value)})}
              sx={{ maxWidth: 200 }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            إجراءات النظام
          </Typography>
          
          <Box display="flex" gap={2}>
            <Button 
              variant="outlined" 
              onClick={handleClearCache}
              startIcon={<RefreshIcon />}
            >
              مسح الكاش
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
